import { cn } from '@/shared/lib/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import { fieldTriggerClassName } from './fieldTrigger';
import { Icon } from './Icon';

export interface SelectOption {
  value: string;
  label: string;
}

/** Radix Select 는 빈 문자열 값을 못 쓴다 — '전체'(미선택)용 내부 값 */
const ALL_VALUE = '__all__';

interface SelectProps {
  /** '담당자' → 트리거에 '담당자: 전체' 로 보인다. 스크린리더 이름으로도 쓴다 */
  label: string;
  value: string | undefined;
  options: SelectOption[];
  onChange: (value: string | undefined) => void;
  /** 미선택 항목. null 이면 '전체' 없이 options 중 하나를 반드시 고른다 */
  allLabel?: string | null;
  /** false 면 트리거에 'label: ' 접두어 없이 값만 보인다 (월 선택 등) */
  showLabel?: boolean;
  /** 트리거 오른쪽 아이콘 — 기본 화살표, 날짜는 달력 */
  icon?: 'chevron' | 'calendar';
  disabled?: boolean;
  className?: string;
}

/**
 * Figma Input(select) + Context Menu.
 * 트리거: 흰 배경 · 1.5px line-grey · radius 16 · 48px, 메뉴: radius 20 · 그림자 500 · 48px 행.
 */
export function Select({
  label,
  value,
  options,
  onChange,
  allLabel = '전체',
  showLabel = true,
  icon = 'chevron',
  disabled,
  className,
}: SelectProps) {
  const items =
    allLabel === null
      ? options
      : [{ value: ALL_VALUE, label: allLabel }, ...options];
  const current = value ?? (allLabel === null ? undefined : ALL_VALUE);
  const currentLabel = items.find((item) => item.value === current)?.label;

  return (
    <SelectPrimitive.Root
      value={current}
      disabled={disabled}
      onValueChange={(next) => onChange(next === ALL_VALUE ? undefined : next)}
    >
      <SelectPrimitive.Trigger
        aria-label={label}
        className={cn(fieldTriggerClassName, className)}
      >
        <span className="truncate text-body-14 font-medium text-fg-primary">
          {showLabel ? `${label}: ` : ''}
          {currentLabel}
        </span>
        {icon === 'calendar' ? (
          <Icon name="calendar" size={24} />
        ) : (
          <>
            <Icon
              name="chevronDown"
              size={24}
              className="group-data-[state=open]:hidden"
            />
            <Icon
              name="chevronUp"
              size={24}
              className="hidden group-data-[state=open]:block"
            />
          </>
        )}
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={8}
          className="z-50 max-h-[var(--radix-select-content-available-height)] min-w-[max(var(--radix-select-trigger-width),240px)] overflow-hidden rounded-20 bg-surface-white py-2 shadow-popover"
        >
          <SelectPrimitive.Viewport>
            {items.map((item) => (
              <SelectPrimitive.Item
                key={item.value}
                value={item.value}
                className={cn(
                  'flex h-12 cursor-pointer items-center gap-3 px-4 text-body-15 font-medium whitespace-nowrap text-fg-primary outline-none',
                  'data-[highlighted]:bg-surface-grey data-[state=checked]:font-semibold data-[state=checked]:text-fg-accent',
                )}
              >
                <span className="size-4 shrink-0">
                  <SelectPrimitive.ItemIndicator>
                    <Icon name="check" size={16} />
                  </SelectPrimitive.ItemIndicator>
                </span>
                <SelectPrimitive.ItemText>
                  {item.label}
                </SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
